<script setup>
// 📦 KARGO KURTARMA v5:
//  - İlerleme çubuğu renkleri artık DOĞRUDAN stil (palet sınıfına bağımlı değil —
//    v4'te renksiz kalmıştı) + gradyanlı dolum, aktif adımda nabız
//  - Dönüş Yolunda kartları da Şubede gibi: tıkla → varyant görselli sipariş detayı
//    (ortak bileşen: RescueCardDetail)
//  - 3 sekme: Şubede / Dönüş Yolunda / Geçmiş (filtre + arama + akıbet izi)
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';
import RescueCardDetail from 'dashboard/components-next/CargoRescue/RescueCardDetail.vue';
import CargoJourneyBar from 'dashboard/components-next/CargoRescue/CargoJourneyBar.vue';

const route = useRoute();
const accountId = route.params.accountId;
const loading = ref(false);
const error = ref('');
const items = ref([]);
const resolved = ref([]);
const stats = ref({});
const busyId = ref(null);
const openId = ref(null);
const tab = ref('subede');
const gecmisFiltre = ref('hepsi');
const gecmisArama = ref('');

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(accountId, 'cargo_rescues');
    items.value = data.items || [];
    resolved.value = data.resolved || [];
    stats.value = data.stats || {};
  } catch (e) {
    error.value = 'Liste yüklenemedi, sayfayı yenileyin.';
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const subede = computed(() => items.value.filter(i => i.at_branch));
const yolda = computed(() => items.value.filter(i => !i.at_branch));
const gecmis = computed(() => {
  let list = resolved.value;
  if (gecmisFiltre.value === 'kurtarildi')
    list = list.filter(i => i.resolution === 'kurtarildi');
  if (gecmisFiltre.value === 'iade_dondu')
    list = list.filter(i => i.resolution === 'iade_dondu');
  const q = gecmisArama.value.trim().toLowerCase();
  if (q)
    list = list.filter(i =>
      `${i.customer_name} ${i.phone} ${i.order_number} ${i.tracking_no}`
        .toLowerCase()
        .includes(q)
    );
  return list;
});

const mark = async (item, payload) => {
  busyId.value = item.id;
  try {
    const updated = await patchMarketplaceJson(
      accountId,
      `cargo_rescues/${item.id}`,
      payload
    );
    Object.assign(item, updated);
  } catch (e) {
    error.value = 'Kaydedilemedi, tekrar deneyin.';
  } finally {
    busyId.value = null;
  }
};

const toggleOpen = item => {
  openId.value = openId.value === item.id ? null : item.id;
};

// Mesaj gitti, 24 saattir müşteri cevabı yok → temsilci aramalı rozeti
const unanswered24h = item =>
  item.notified_at &&
  !item.customer_reply &&
  Date.now() - new Date(item.notified_at).getTime() > 24 * 3600 * 1000;

// Telefon görünümü tek biçim: "0538 308 56 81" (tel: linki ham değeri kullanır)
const fmtPhone = p => {
  const d = String(p || '')
    .replace(/\D/g, '')
    .replace(/^90/, '')
    .replace(/^0+/, '');
  if (!/^5\d{9}$/.test(d)) return p || '—';
  return `0${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8)}`;
};

// Geçmiş sekmesi akıbet çubuğu — renkler garantili olsun diye doğrudan hex.
// Kırmızı SADECE kesinleşmiş kötü akıbet (iade döndü); süreçtekiler nötr mavi.
const TONES = {
  good: { solid: '#16a34a', grad: 'linear-gradient(90deg,#16a34a,#4ade80)' },
  warn: { solid: '#d97706', grad: 'linear-gradient(90deg,#d97706,#fbbf24)' },
  bad: { solid: '#b91c1c', grad: 'linear-gradient(90deg,#b91c1c,#f87171)' },
  info: { solid: '#2563eb', grad: 'linear-gradient(90deg,#2563eb,#60a5fa)' },
};
const stepsFor = item => {
  if (item.resolution === 'kurtarildi')
    return {
      steps: ['Teslim edilemedi', 'Şubede beklendi', 'Müşteri teslim aldı 🎉'],
      current: 2,
      tone: 'good',
    };
  if (item.resolution === 'iade_dondu')
    return {
      steps: ['Teslim edilemedi', 'Şubede beklendi', 'Dönüş yolu', 'Depoya döndü'],
      current: 3,
      tone: 'bad',
    };
  if (item.at_branch)
    return {
      steps: ['Teslim edilemedi', 'Şubede bekliyor', 'Dönüş yolu', 'Depo'],
      current: 1,
      tone: 'warn',
    };
  return {
    steps: ['Teslim edilemedi', 'Şubede beklendi', 'Hareket halinde', 'Akıbet'],
    current: 2,
    tone: 'info',
  };
};
const fillPct = s => (s.current / (s.steps.length - 1)) * 100;

const saat = t =>
  t
    ? new Date(t).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
const tl = v => (v || 0).toLocaleString('tr-TR');
const RESULTS = [
  { key: 'alacak', label: '✅ Alacak', style: 'background:#16a34a;color:#fff' },
  {
    key: 'ulasilamadi',
    label: '📵 Ulaşılamadı',
    style: 'background:#d97706;color:#fff',
  },
  {
    key: 'istemiyor',
    label: '🚫 İstemiyor',
    style: 'background:#b91c1c;color:#fff',
  },
];
const RESULT_TR = {
  alacak: '✅ alacak demişti',
  ulasilamadi: '📵 ulaşılamadı',
  istemiyor: '🚫 istemiyor demişti',
};
</script>

<template>
  <div class="flex flex-col h-full overflow-auto p-4 bg-n-background">
    <div class="max-w-4xl w-full mx-auto">
      <h1 class="text-2xl font-bold text-n-slate-12">📦 Kargo Kurtarma</h1>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-2 my-4">
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-extrabold" style="color: #b91c1c">
            {{ stats.subede || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">
            şubede · {{ stats.aranmamis || 0 }} aranmadı
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-extrabold text-n-slate-12">
            {{ stats.yolda || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">hareket halinde</div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-extrabold text-n-slate-12">
            {{ tl(stats.risk_tl) }} ₺
          </div>
          <div class="text-xs text-n-slate-11">risk altında</div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-extrabold" style="color: #16a34a">
            🎉 {{ stats.hafta_kurtarilan || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">
            bu hafta kurtarılan · dönen {{ stats.hafta_donen || 0 }}
          </div>
        </div>
      </div>

      <div class="flex gap-1 mb-4 border-b border-n-weak">
        <button
          v-for="t in [
            { k: 'subede', l: `📍 Şubede (${subede.length})` },
            { k: 'yolda', l: `🚛 Hareket Halinde (${yolda.length})` },
            { k: 'gecmis', l: `🗂️ Geçmiş (${resolved.length})` },
          ]"
          :key="t.k"
          class="px-4 py-2 font-bold text-sm rounded-t-lg"
          :class="
            tab === t.k
              ? 'bg-n-solid-1 text-n-slate-12 border border-n-weak border-b-0'
              : 'text-n-slate-11'
          "
          @click="tab = t.k"
        >
          {{ t.l }}
        </button>
      </div>

      <p v-if="error" class="text-ruby-800 mb-2">{{ error }}</p>
      <p v-if="loading" class="text-n-slate-11">Yükleniyor…</p>

      <!-- ============ ŞUBEDE ============ -->
      <template v-if="tab === 'subede'">
        <div
          v-for="item in subede"
          :key="item.id"
          class="rounded-xl border bg-n-solid-1 mb-2 overflow-hidden"
          :style="
            item.call_result ? 'border-color:#86efac' : 'border-color:#fca5a5'
          "
        >
          <button
            class="w-full text-left p-3 flex flex-wrap items-center justify-between gap-2"
            @click="toggleOpen(item)"
          >
            <div class="min-w-0">
              <div class="font-bold text-n-slate-12">
                <span class="text-n-slate-10 mr-1">{{
                  openId === item.id ? '▾' : '▸'
                }}</span>
                {{ item.customer_name || 'İsimsiz' }}
                <span
                  v-if="item.repeat_count >= 2"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fee2e2; color: #b91c1c"
                  >⚠️ {{ item.repeat_count }}. kez almıyor</span
                >
                <span
                  v-if="item.return_history_count"
                  class="ml-1 text-xs font-semibold text-n-slate-11 bg-n-slate-3 px-2 py-0.5 rounded-full"
                  >↩️ {{ item.return_history_count }} iade kaydı</span
                >
                <span
                  v-if="item.on_vehicle_today"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse"
                  style="background: #dbeafe; color: #1d4ed8"
                  >🚚 bugün tekrar dağıtımda</span
                >
                <span
                  v-if="item.customer_reply === 'alacak'"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #dcfce7; color: #15803d"
                  >💬 &quot;alacağım&quot; dedi</span
                >
                <span
                  v-else-if="item.customer_reply === 'almayacak'"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fee2e2; color: #b91c1c"
                  >💬 &quot;almayacağım&quot; dedi</span
                >
                <span
                  v-else-if="unanswered24h(item)"
                  class="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                  style="background: #e5e7eb; color: #4b5563"
                  >✉️ mesaj cevapsız</span
                >
              </div>
              <div
                v-if="item.customer_reason"
                class="text-xs font-semibold mt-0.5"
                style="color: #b91c1c"
              >
                Müşteri nedeni: “{{ item.customer_reason }}”
              </div>
              <div class="text-sm text-n-slate-11">
                {{ tl(item.amount) }} ₺ ·
                {{ item.order_number || item.tracking_no }} · Şube:
                {{ item.dest_branch || item.branch || '—' }}
              </div>
            </div>
            <div class="text-right shrink-0">
              <a
                :href="`tel:${item.phone}`"
                class="font-bold text-lg"
                style="color: #2563eb"
                @click.stop
                >📞 {{ fmtPhone(item.phone) }}</a
              >
            </div>
          </button>

          <div class="flex flex-wrap items-center gap-2 px-3 pb-3">
            <button
              v-for="r in RESULTS"
              :key="r.key"
              class="px-3 py-2 rounded-lg text-sm font-bold"
              :style="
                item.call_result === r.key
                  ? r.style
                  : 'background:#e5e7eb;color:#111'
              "
              :disabled="busyId === item.id"
              @click="
                mark(item, {
                  call_result: item.call_result === r.key ? '' : r.key,
                })
              "
            >
              {{ r.label }}
            </button>
            <span v-if="item.called_at" class="text-xs text-n-slate-10">
              işaretleyen: {{ item.called_by }} · {{ saat(item.called_at) }}
            </span>
          </div>

          <RescueCardDetail
            v-if="openId === item.id"
            :item="item"
            :account-id="accountId"
          />
        </div>
        <p v-if="!loading && !subede.length" class="text-n-slate-11">
          Şubede bekleyen paket yok 🎉
        </p>
      </template>

      <!-- ============ DÖNÜŞ YOLUNDA ============ -->
      <template v-if="tab === 'yolda'">
        <div
          v-for="item in yolda"
          :key="item.id"
          class="rounded-xl border border-n-weak bg-n-solid-1 mb-2 overflow-hidden"
        >
          <button
            class="w-full text-left p-3"
            @click="toggleOpen(item)"
          >
            <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div class="font-bold text-n-slate-12">
                <span class="text-n-slate-10 mr-1">{{
                  openId === item.id ? '▾' : '▸'
                }}</span>
                {{ item.customer_name || item.tracking_no }}
                <span
                  v-if="item.repeat_count >= 2"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fee2e2; color: #b91c1c"
                  >⚠️ {{ item.repeat_count }}. kez</span
                >
                <span
                  v-if="item.direction === 'donus'"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #ede9fe; color: #6d28d9"
                  >↩️ bize dönüyor</span
                >
                <span
                  v-else-if="item.direction === 'musteriye'"
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fef3c7; color: #b45309"
                  >🚚 müşteriye tekrar gidiyor olabilir</span
                >
                <span
                  v-else
                  class="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                  style="background: #e5e7eb; color: #4b5563"
                  >❓ yönü netleşiyor</span
                >
              </div>
              <div class="text-sm text-n-slate-11">
                {{ tl(item.amount) }} ₺ · {{ item.order_number || '—' }}
              </div>
            </div>

            <!-- TEK yolculuk çubuğu (detayla ortak bileşen; nötr mavi) -->
            <CargoJourneyBar :item="item" />
            <div class="text-xs text-n-slate-10 mt-2">
              Kargo durumu: {{ item.cargo_status }} · takip
              {{ item.tracking_no }}
            </div>
          </button>

          <RescueCardDetail
            v-if="openId === item.id"
            :item="item"
            :account-id="accountId"
            :show-journey="false"
          />
        </div>
        <p v-if="!loading && !yolda.length" class="text-n-slate-11">
          Hareket halinde paket yok 🎉
        </p>
      </template>

      <!-- ============ GEÇMİŞ ============ -->
      <template v-if="tab === 'gecmis'">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <button
            v-for="f in [
              { k: 'hepsi', l: 'Tümü' },
              { k: 'kurtarildi', l: '🎉 Kurtarılan' },
              { k: 'iade_dondu', l: '↩️ Dönen' },
            ]"
            :key="f.k"
            class="px-3 py-1.5 rounded-full text-sm font-semibold"
            :style="
              gecmisFiltre === f.k
                ? 'background:#111;color:#fff'
                : 'background:#e5e7eb;color:#374151'
            "
            @click="gecmisFiltre = f.k"
          >
            {{ f.l }}
          </button>
          <input
            v-model="gecmisArama"
            placeholder="isim / telefon / sipariş ara…"
            class="flex-1 min-w-[180px] px-3 py-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
          />
        </div>

        <div
          v-for="item in gecmis"
          :key="item.id"
          class="rounded-xl border bg-n-solid-1 p-3 mb-2"
          :style="
            item.resolution === 'kurtarildi'
              ? 'border-color:#86efac'
              : 'border-color:#e5e7eb'
          "
        >
          <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div class="min-w-0">
              <div class="font-bold text-n-slate-12">
                {{ item.customer_name || item.tracking_no }}
                <span
                  class="ml-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  :style="
                    item.resolution === 'kurtarildi'
                      ? 'background:#dcfce7;color:#15803d'
                      : item.resolution === 'iade_dondu'
                        ? 'background:#fee2e2;color:#b91c1c'
                        : 'background:#e5e7eb;color:#374151'
                  "
                >
                  {{
                    item.resolution === 'kurtarildi'
                      ? '🎉 KURTARILDI'
                      : item.resolution === 'iade_dondu'
                        ? '↩️ İADE DÖNDÜ'
                        : '⏳ netleşiyor'
                  }}
                </span>
              </div>
              <div class="text-sm text-n-slate-11">
                {{ tl(item.amount) }} ₺ · {{ item.order_number || '—' }} ·
                kapanış: {{ item.resolved_at }}
              </div>
              <div class="text-xs text-n-slate-10 mt-0.5">
                <template v-if="item.called_at">
                  ☎️ {{ item.called_by }} aradı ·
                  {{ RESULT_TR[item.call_result] || 'sonuç girilmedi' }}
                </template>
                <template v-else>aranmadan sonuçlandı</template>
              </div>
            </div>
          </div>
          <div v-for="s in [stepsFor(item)]" :key="item.id + '-gbar'">
            <div
              class="h-1.5 rounded-full overflow-hidden"
              style="background: #e5e7eb"
            >
              <div
                class="h-full rounded-full"
                :style="`width:${fillPct(s)}%;background:${TONES[s.tone].grad}`"
              />
            </div>
          </div>
        </div>
        <p v-if="!loading && !gecmis.length" class="text-n-slate-11">
          Bu filtrede kayıt yok.
        </p>
      </template>
    </div>
  </div>
</template>
